import { useMemo } from 'react';
import { MetricTimeseriesData } from '@/services/MetricsService';
import { PercentageMetrics } from '@/components/OrbusStreamNavigatorComponent/lib/PercentageMetrics';
import { Metric } from '@/types/metrics';

type TimeRangeLevel = 'week' | 'month' | 'threeMonths' | 'sixMonths' | 'year';

export interface MetricCalculationsInput {
  metricValueKey: string;
  operation: string;
  metricOperation?: string;
  target?: Array<{ startTs: number; endTs: number; value: number }> | null;
  decreasingTarget?: boolean | null;
  alertingThresholdPercentage?: number | null;
  timeseriesData: MetricTimeseriesData | null;
  dimensionItemName?: string;
  currentDate?: Date;
}

export interface TargetProgress {
  percentage: number;
  isOnTrack: boolean;
  targetValue: number;
}

export interface MetricCalculationsResult {
  chartData: [number, number][];
  currentValue: number | string | Record<string, number>;
  cumulativeValue: number | null;
  cumulativeSeries: Array<{ startTs: number; endTs: number; value: number }>;
  hasChartData: boolean;
  targetProgress: TargetProgress | null;
}

// Helper function to map metric keys to API keys
const getApiMetricKey = (metricKey: string): string => {
  // Direct alias mapping for keys that are known to differ between Capsifi and the timeseries blob
  const keyMapping: Record<string, string> = {
    "deal_size": "average_deal_size",
    "lead_count": "total_leads",
    "sales_cycle": "avg_sales_cycle",
    "revenue": "total_revenue",
    "deals": "deal_count"
  };

  // Return the alias if one exists, otherwise use the original key as-is.
  // NOTE: Do NOT add fuzzy/substring matching here — it breaks dimensional
  // metric keys like "leads_by_application" by incorrectly mapping them.
  return keyMapping[metricKey] || metricKey;
};

// Helper function to transform timeseries data into chart format
const transformTimeseriesData = (data: MetricTimeseriesData | null, metricKey: string, dimensionItemName?: string): [number, number][] => {
  if (!data) return [];
  
  const apiKey = getApiMetricKey(metricKey);
  
  // Check if the data has the direct metric key
  if (apiKey in data) {
    const metricData = data[apiKey as keyof typeof data];
    
    // Check if this is an array of timestamp-value pairs
    if (Array.isArray(metricData) && metricData.length > 0 && Array.isArray(metricData[0])) {
      // If dimensionItemName is provided but data is a flat array (not nested by dimension),
      // we can't filter by dimension - return empty to avoid showing wrong data
      if (dimensionItemName) {
        return [];
      }
      return metricData as [number, number][];
    }
    
    // If it's a nested object with sources or other breakdowns (e.g., by dimension item name)
    if (typeof metricData === 'object' && !Array.isArray(metricData)) {
      // If dimensionItemName is provided, filter by that specific item only
      if (dimensionItemName) {
        if (dimensionItemName in (metricData as object)) {
          const itemData = (metricData as any)[dimensionItemName];
          if (Array.isArray(itemData)) {
            return itemData as [number, number][];
          }
        }
        // No match for the specific dimension item - return empty
        console.warn(`No data found for dimension item ${dimensionItemName} in ${apiKey}`);
        return [];
      }
      
      // No dimensionItemName specified - take the first item's data as default
      const firstKey = Object.keys(metricData)[0];
      if (firstKey && Array.isArray((metricData as any)[firstKey])) {
        return (metricData as any)[firstKey] as [number, number][];
      }
    }
  }
  
  return [];
};

// Helper function to get current value from timeseries data
const getCurrentValue = (data: MetricTimeseriesData | null, metricKey: string, dimensionItemName?: string, currentDate?: Date): number | string => {
  if (!data) return 'N/A';
  
  const apiKey = getApiMetricKey(metricKey);
  
  // Check if the data has the direct metric key
  if (apiKey in data) {
    const metricData = data[apiKey as keyof typeof data];
    
    // If it's an array of timestamp-value pairs, get the last one
    if (Array.isArray(metricData) && metricData.length > 0 && Array.isArray(metricData[0])) {
      // If dimensionItemName is provided but data is a flat array (not nested by dimension),
      // we can't filter by dimension - return N/A to avoid showing wrong data
      if (dimensionItemName) {
        return 'N/A';
      }
      // Find data point closest to today's date (only past dates)
      const nowTimestamp = currentDate ? Math.floor(currentDate.getTime() / 1000) : Math.floor(Date.now() / 1000);
      const pastData = (metricData as [number, number][])
        .filter(([timestamp]) => timestamp <= nowTimestamp);
      
      if (pastData.length > 0) {
        const closestData = pastData.reduce((closest, current) => {
          const closestDiff = Math.abs(closest[0] - nowTimestamp);
          const currentDiff = Math.abs(current[0] - nowTimestamp);
          return currentDiff < closestDiff ? current : closest;
        });
        return closestData[1];
      }
    }
    
    // If it's a nested object with sources or other breakdowns (e.g., by dimension item name)
    if (typeof metricData === 'object' && !Array.isArray(metricData)) {
      // If dimensionItemName is provided, get value only from that specific item
      if (dimensionItemName) {
        if (dimensionItemName in (metricData as object)) {
          const itemData = (metricData as any)[dimensionItemName];
          if (Array.isArray(itemData) && itemData.length > 0) {
            const nowTimestamp = currentDate ? Math.floor(currentDate.getTime() / 1000) : Math.floor(Date.now() / 1000);
            const pastData = itemData.filter((entry: [number, number]) => entry[0] <= nowTimestamp);
            if (pastData.length > 0) {
              const closestEntry = pastData.reduce((closest: [number, number], current: [number, number]) => {
                const closestDiff = Math.abs(closest[0] - nowTimestamp);
                const currentDiff = Math.abs(current[0] - nowTimestamp);
                return currentDiff < closestDiff ? current : closest;
              });
              // Found value from dimension item closest to today
              return closestEntry[1];
            }
          }
        }
        // No match for the specific dimension item - return N/A
        // No value found for dimension item
        return 'N/A';
      }
      
      // No dimensionItemName specified - get all values across all sources closest to today
      const nowTimestamp = currentDate ? Math.floor(currentDate.getTime() / 1000) : Math.floor(Date.now() / 1000);
      const allValues: { timestamp: number; value: number }[] = [];
      
      Object.keys(metricData as object).forEach(source => {
        const sourceData = (metricData as any)[source];
        if (Array.isArray(sourceData) && sourceData.length > 0) {
          // Filter out zero values and future dates, then find closest to today
          const pastNonZeroData = sourceData.filter((entry: [number, number]) => 
            entry[1] !== 0 && entry[0] <= nowTimestamp
          );
          if (pastNonZeroData.length > 0) {
            const closestEntry = pastNonZeroData.reduce((closest: [number, number], current: [number, number]) => {
              const closestDiff = Math.abs(closest[0] - nowTimestamp);
              const currentDiff = Math.abs(current[0] - nowTimestamp);
              return currentDiff < closestDiff ? current : closest;
            });
            allValues.push({ timestamp: closestEntry[0], value: closestEntry[1] });
          }
        }
      });
      
      if (allValues.length > 0) {
        // Find the value with timestamp closest to today
        const closestValue = allValues.reduce((closest, current) => {
          const closestDiff = Math.abs(closest.timestamp - nowTimestamp);
          const currentDiff = Math.abs(current.timestamp - nowTimestamp);
          return currentDiff < closestDiff ? current : closest;
        });
        // Found value in nested object closest to today
        return closestValue.value;
      }
    }
  }
  
  // No value found
  return 'N/A';
};

// Return the target from the data point closest to the reference date in the past
// (i.e. the segment with the largest startTs that is <= reference date).
export const getCurrentPeriodTarget = (segments: Array<{ startTs: number; endTs: number; value: number }> | null | undefined, currentDate?: Date): number | null => {
  if (!segments || segments.length === 0) return null;
  const nowSec = currentDate ? Math.floor(currentDate.getTime() / 1000) : Math.floor(Date.now() / 1000);
  let best: { startTs: number; endTs: number; value: number } | null = null;
  for (const s of segments) {
    if (s.startTs <= nowSec && (!best || s.startTs > best.startTs)) {
      best = s;
    }
  }
  return best?.value ?? null;
};

// Pure function (no hooks) that computes calculations for a single metric
export function computeMetricCalculations({
  metricValueKey,
  operation,
  metricOperation,
  target,
  decreasingTarget,
  alertingThresholdPercentage,
  timeseriesData,
  dimensionItemName,
  currentDate,
}: MetricCalculationsInput): MetricCalculationsResult {
  const refTimestampForFilter = Math.floor((currentDate ?? new Date()).getTime() / 1000);
  const chartData = timeseriesData
    ? transformTimeseriesData(timeseriesData, metricValueKey, dimensionItemName)
        .filter(([ts]) => ts <= refTimestampForFilter)
    : [];

  const currentValue = getCurrentValue(timeseriesData, metricValueKey, dimensionItemName, currentDate);

  let cumulativeValue: number | null = null;
  const cumulativeSeries: Array<{ startTs: number; endTs: number; value: number }> = [];
  if (chartData.length) {
    const op = (metricOperation || operation || '').toLowerCase();
    const opAverage = op === 'average';
    const opTotal = op === 'total';

    if (opAverage || opTotal) {
      const refDate = currentDate ?? new Date();
      const refTimestamp = Math.floor(refDate.getTime() / 1000);
      
      // Find the most recent data point up to the reference date
      const pastData = chartData.filter(([timestamp]) => timestamp <= refTimestamp);
      
      if (pastData.length > 0) {
        // Sort by timestamp and get the most recent point
        const sortedData = [...pastData].sort((a, b) => b[0] - a[0]);
        const mostRecentTimestamp = sortedData[0][0];
        const mostRecentYear = new Date(mostRecentTimestamp * 1000).getFullYear();
        
        // Calculate cumulative for the year of the most recent data point
        let cumulative = 0;
        let yearSum = 0;
        let yearCount = 0;
        
        chartData.forEach(([timestamp, value], idx) => {
          if (timestamp <= mostRecentTimestamp && new Date(timestamp * 1000).getFullYear() === mostRecentYear) {
            if (opAverage) {
              yearSum += value;
              yearCount += 1;
              cumulative = yearCount > 0 ? yearSum / yearCount : 0;
            } else {
              cumulative += value;
            }
            const nextTs = idx + 1 < chartData.length ? chartData[idx + 1][0] : timestamp + 86400 * 365;
            cumulativeSeries.push({ startTs: timestamp, endTs: nextTs, value: cumulative });
          }
        });
        cumulativeValue = cumulative;
      }
    }
  }

  const hasChartData = chartData.length > 0;

  let targetProgress: TargetProgress | null = null;
  if (cumulativeValue !== null && target && target.length > 0) {
    // Use the target segment matching the current date; fall back to target[0] for historical views
    const currentPeriodValue = getCurrentPeriodTarget(target, currentDate);
    const targetValue = currentPeriodValue ?? target[0].value;

    const offset = alertingThresholdPercentage ?? 0;
    // Normalise: if actual is below the offset, clamp to 1 to avoid negative/zero numerators
    const adjustedActual = offset && cumulativeValue < offset ? 1 : cumulativeValue - offset;
    const adjustedTarget = targetValue - offset;

    const rawPercentage = adjustedTarget > 0
      ? (adjustedActual / adjustedTarget) * 100
      : (cumulativeValue / targetValue) * 100;
    // For decreasing targets, convert to rising-target equivalent so aggregation is meaningful:
    // e.g. 150% raw (off-track) becomes (100/150)*100 ≈ 67% (clearly off-track)
    const percentage = Math.round(
      decreasingTarget ? (100 / rawPercentage) * 100 : rawPercentage
    );
    const isOnTrack = decreasingTarget
      ? cumulativeValue <= targetValue
      : cumulativeValue >= targetValue;
    targetProgress = { percentage, isOnTrack, targetValue };
  }

  if (sessionStorage.getItem('loadPocData') === 'true') {
    targetProgress = {
      percentage: Math.floor(Math.random() * 145) + 10,
      isOnTrack: Math.random() > 0.5,
      targetValue: targetProgress?.targetValue ?? 100,
    };
  }

  return { chartData, currentValue, cumulativeValue, cumulativeSeries, hasChartData, targetProgress };
}

// Helper function to generate a composite key for unique metric identification
export function getMetricCompositeKey(
  metricValueKey: string,
  metricId?: string
): string {
  return metricId ? `${metricValueKey}::${metricId}` : metricValueKey;
}

// Metric descriptor used by useMetricCalculationsMap
export interface MetricDescriptor {
  metricValueKey: string;
  operation: string;
  metricOperation?: string;
  target?: Array<{ startTs: number; endTs: number; value: number }> | null;
  decreasingTarget?: boolean | null;
  alertingThresholdPercentage?: number | null;
  Id?: string;
}

// Hook that computes calculations for an array of metrics in one useMemo pass
export function useMetricCalculationsMap(
  metrics: MetricDescriptor[],
  timeseriesData: MetricTimeseriesData | null,
  timeRange: TimeRangeLevel,
  showKPIMode: boolean,
  dimensionItemName?: string,
  currentDate?: Date,
): Map<string, MetricCalculationsResult> {
  return useMemo(() => {
    const map = new Map<string, MetricCalculationsResult>();
    for (const m of metrics) {
      const compositeKey = getMetricCompositeKey(m.metricValueKey, m.Id);
      map.set(
        compositeKey,
        computeMetricCalculations({
          metricValueKey: m.metricValueKey,
          operation: m.operation,
          metricOperation: m.metricOperation,
          target: m.target,
          decreasingTarget: m.decreasingTarget,
          alertingThresholdPercentage: m.alertingThresholdPercentage,
          timeseriesData,
          dimensionItemName,
          currentDate,
        }),
      );
    }
    return map;
  }, [metrics, timeseriesData, timeRange, showKPIMode, dimensionItemName, currentDate]);
}

export function useMetricCalculations({
  metricValueKey,
  operation,
  metricOperation,
  target,
  decreasingTarget,
  alertingThresholdPercentage,
  timeseriesData,
  dimensionItemName,
  currentDate,
}: MetricCalculationsInput): MetricCalculationsResult {
  // Memoize chart data transformation - filter by dimension item name and to past-only data
  // so the chart and targetProgress agree on what counts as "now"
  const chartData = useMemo(() => {
    if (!timeseriesData) return [];
    const refTs = Math.floor((currentDate ?? new Date()).getTime() / 1000);
    return transformTimeseriesData(timeseriesData, metricValueKey, dimensionItemName)
      .filter(([ts]) => ts <= refTs);
  }, [timeseriesData, metricValueKey, dimensionItemName, currentDate]);
  
  const currentValue = useMemo(() =>
      getCurrentValue(timeseriesData, metricValueKey, dimensionItemName, currentDate),
    [metricValueKey, timeseriesData, dimensionItemName, currentDate]
  );
  
  const { cumulativeValue, cumulativeSeries } = useMemo((): { cumulativeValue: number | null; cumulativeSeries: Array<{ startTs: number; endTs: number; value: number }> } => {
    if (!chartData.length) return { cumulativeValue: null, cumulativeSeries: [] };
    
    const op = (metricOperation || operation || '').toLowerCase();
    const opAverage = op === 'average';
    const opTotal = op === 'total';
    
    if (!opAverage && !opTotal) return { cumulativeValue: null, cumulativeSeries: [] };
    
    const refDate = currentDate ?? new Date();
    const refTimestamp = Math.floor(refDate.getTime() / 1000);
    
    // Find the most recent data point up to the reference date
    const pastData = chartData.filter(([timestamp]) => timestamp <= refTimestamp);
    
    if (pastData.length === 0) return null;
    
    // Sort by timestamp and get the most recent point
    const sortedData = [...pastData].sort((a, b) => b[0] - a[0]);
    const mostRecentTimestamp = sortedData[0][0];
    const mostRecentYear = new Date(mostRecentTimestamp * 1000).getFullYear();
    
    // Calculate cumulative for the year of the most recent data point
    let cumulative = 0;
    let yearSum = 0;
    let yearCount = 0;
    const series: Array<{ startTs: number; endTs: number; value: number }> = [];
    
    chartData.forEach(([timestamp, value], idx) => {
      if (timestamp <= mostRecentTimestamp && new Date(timestamp * 1000).getFullYear() === mostRecentYear) {
        if (opAverage) {
          yearSum += value;
          yearCount += 1;
          cumulative = yearCount > 0 ? yearSum / yearCount : 0;
        } else {
          cumulative += value;
        }
        const nextTs = idx + 1 < chartData.length ? chartData[idx + 1][0] : timestamp + 86400 * 365;
        series.push({ startTs: timestamp, endTs: nextTs, value: cumulative });
      }
    });
    
    return { cumulativeValue: cumulative, cumulativeSeries: series };
  }, [chartData, metricOperation, operation, currentDate]);
  
  const hasChartData = chartData.length > 0;
  
  // Target progress: percentage + on-track status
  const targetProgress = useMemo((): TargetProgress | null => {
    if (cumulativeValue === null || !target || target.length === 0) return null;
    // Use the target segment matching the current date; fall back to target[0] for historical views
    const currentPeriodValue = getCurrentPeriodTarget(target, currentDate);
    const targetValue = currentPeriodValue ?? target[0].value;

    const offset = alertingThresholdPercentage ?? 0;
    // Normalise: if actual is below the offset, clamp to 1 to avoid negative/zero numerators
    const adjustedActual = offset && cumulativeValue < offset ? 1 : cumulativeValue - offset;
    const adjustedTarget = targetValue - offset;

    const rawPercentage = adjustedTarget > 0
      ? (adjustedActual / adjustedTarget) * 100
      : (cumulativeValue / targetValue) * 100;
    // For decreasing targets, convert to rising-target equivalent so aggregation is meaningful
    const percentage = Math.round(
      decreasingTarget ? (100 / rawPercentage) * 100 : rawPercentage
    );
    const isOnTrack = decreasingTarget
      ? cumulativeValue <= targetValue
      : cumulativeValue >= targetValue;
    return { percentage, isOnTrack, targetValue };
  }, [cumulativeValue, target, decreasingTarget, alertingThresholdPercentage, currentDate]);

  return {
    chartData,
    currentValue,
    cumulativeValue,
    cumulativeSeries,
    hasChartData,
    targetProgress,
  };
}

const CATEGORY_FIELDS: Record<string, keyof Pick<PercentageMetrics,
  'TimePercentage' | 'VolumePercentage' | 'QualityPercentage' |
  'SentimentPercentage' | 'CostPercentage' | 'RiskPercentage'>> = {
  time: 'TimePercentage',
  volume: 'VolumePercentage',
  quality: 'QualityPercentage',
  sentiment: 'SentimentPercentage',
  cost: 'CostPercentage',
  risk: 'RiskPercentage',
};

/**
 * Graph data subset needed for capability-to-child mapping.
 */
interface GraphDataForPercentages {
  teams?: Array<{ team: string; capability: string; stage?: string }>;
  applicationGroups?: Array<{ ApplicationGroup: string; Team: string; Capability?: string }>;
  leafs?: Array<{ Leaf: string; Application: string; Team: string; Stage: string; Capability: string; Percentage: PercentageMetrics }>;
}

/**
 * Builds a PercentageMetrics per node name (dimension items AND capabilities).
 *
 * For dimension items (apps, teams, products):
 *   Average targetProgress.percentage across all dimensional metrics of a given category.
 *
 * For capability nodes:
 *   Per performance category:
 *     1. Native %  = avg of non-zero targetProgress.percentage from non-dimensional metrics
 *                     whose "Business Capability" matches the capability name.
 *     2. Child %   = avg of non-zero child-node category percentages (apps + teams under this capability).
 *     3. Blended % = avg of ALL non-zero values (native + child) for that category.
 */
export function computeNodePercentages(
  metrics: Metric[],
  allMetricCalculations: Map<string, MetricCalculationsResult>,
  dimensionItemNames: Record<string, Set<string>>,
  graphData?: GraphDataForPercentages | null,
): Map<string, PercentageMetrics> {
  const result = new Map<string, PercentageMetrics>();
  if (allMetricCalculations.size === 0) return result;

  // ─── Step 1: Compute dimension-item (app/team/product) percentages ───
  // Aggregation: dimensionItemName -> (category -> { sum, count })
  const aggregateByItemThenCategory = new Map<
    string,
    Map<string, { sum: number; count: number }>
  >();

  for (const metric of metrics) {
    const metricCategory = (metric.metricCategory || '').toLowerCase();
    if (!metricCategory || !(metricCategory in CATEGORY_FIELDS)) continue;

    const dimensionSuffix = metric.metricDimension
      ? Object.keys(dimensionItemNames).find(suffix => metric.metricDimension!.endsWith(suffix))
      : undefined;
    if (!dimensionSuffix) continue;

    for (const dimensionItemName of dimensionItemNames[dimensionSuffix]) {
      const calculationKey = `${getMetricCompositeKey(metric.metricValueKey, metric.Id)}::${dimensionItemName}`;
      const calculation = allMetricCalculations.get(calculationKey);
      if (!calculation?.targetProgress) continue;
      if (!calculation.targetProgress.percentage) continue; // skip zero/null/undefined

      if (!aggregateByItemThenCategory.has(dimensionItemName)) {
        aggregateByItemThenCategory.set(dimensionItemName, new Map());
      }
      const aggregateByCategory = aggregateByItemThenCategory.get(dimensionItemName)!;
      if (!aggregateByCategory.has(metricCategory)) {
        aggregateByCategory.set(metricCategory, { sum: 0, count: 0 });
      }
      const agg = aggregateByCategory.get(metricCategory)!;
      agg.sum += calculation.targetProgress.percentage;
      agg.count += 1;
    }
  }

  // Track which dimension items have any chart data at all
  const itemsWithTimeseriesData = new Set<string>();
  for (const metric of metrics) {
    const dimensionSuffix = metric.metricDimension
      ? Object.keys(dimensionItemNames).find(suffix => metric.metricDimension!.endsWith(suffix))
      : undefined;
    if (!dimensionSuffix) continue;

    for (const dimensionItemName of dimensionItemNames[dimensionSuffix]) {
      const calculationKey = `${getMetricCompositeKey(metric.metricValueKey, metric.Id)}::${dimensionItemName}`;
      const calculation = allMetricCalculations.get(calculationKey);
      if (calculation?.hasChartData) {
        itemsWithTimeseriesData.add(dimensionItemName);
      }
    }
  }

  // Convert dimension-item aggregates into PercentageMetrics
  for (const [dimensionItemName, aggregateByCategory] of aggregateByItemThenCategory) {
    const hasData = itemsWithTimeseriesData.has(dimensionItemName);
    const percentageMetrics = new PercentageMetrics(
      0, 0, 0, 0, 0, 0, 
      hasData,
      aggregateByCategory.has('time'),
      aggregateByCategory.has('volume'),
      aggregateByCategory.has('quality'),
      aggregateByCategory.has('sentiment'),
      aggregateByCategory.has('cost'),
      aggregateByCategory.has('risk')
    );
    for (const [category, { sum, count }] of aggregateByCategory) {
      const field = CATEGORY_FIELDS[category];
      if (field && count > 0) {
        const avg = Math.round(sum / count);
        percentageMetrics[field] = avg;
      }
    }
    result.set(dimensionItemName, percentageMetrics);
  }

  // For dimension items that have NO aggregated percentages at all,
  // create a PercentageMetrics with hasTimeseriesData=false so the label turns grey
  for (const dimensionSuffix of Object.keys(dimensionItemNames)) {
    for (const dimensionItemName of dimensionItemNames[dimensionSuffix]) {
      if (!result.has(dimensionItemName)) {
        const hasData = itemsWithTimeseriesData.has(dimensionItemName);
        result.set(dimensionItemName, new PercentageMetrics(0, 0, 0, 0, 0, 0, hasData, false, false, false, false, false, false));
      }
    }
  }

  // Add leaf node percentages from graph data (they come pre-computed from API)
  // Use composite key to avoid collapsing duplicate leaf names across different app/team combos
  if (graphData?.leafs) {
    for (const leaf of graphData.leafs) {
      if (leaf.Leaf && leaf.Percentage) {
        const leafKey = `${leaf.Leaf}-${leaf.Application}-${leaf.Team}-${leaf.Capability}-${leaf.Stage}`;
        result.set(leafKey, leaf.Percentage);
      }
    }
  }

  // ─── Step 2: Compute capability-node percentages (native + child blend) ───

  // Build capability -> child result-map keys (array, NOT set — duplicates are intentional
  // because the same app/team can appear under multiple parents and should count each time)
  const capabilityChildren = new Map<string, string[]>();
  if (graphData?.teams) {
    for (const t of graphData.teams) {
      if (!t.capability || !t.team) continue;
      if (!capabilityChildren.has(t.capability)) {
        capabilityChildren.set(t.capability, []);
      }
      // result-map key for teams is bare team name
      capabilityChildren.get(t.capability)!.push(t.team);
    }
  }
  if (graphData?.applicationGroups) {
    for (const ag of graphData.applicationGroups) {
      if (!ag.Capability || !ag.ApplicationGroup) continue;
      if (!capabilityChildren.has(ag.Capability)) {
        capabilityChildren.set(ag.Capability, []);
      }
      // result-map key for apps is bare ApplicationGroup name
      capabilityChildren.get(ag.Capability)!.push(ag.ApplicationGroup);
    }
  }
  if (graphData?.leafs) {
    for (const leaf of graphData.leafs) {
      if (!leaf.Capability || !leaf.Leaf) continue;
      // result-map key for leafs is composite (each instance is unique)
      const leafKey = `${leaf.Leaf}-${leaf.Application}-${leaf.Team}-${leaf.Capability}-${leaf.Stage}`;
      if (!capabilityChildren.has(leaf.Capability)) {
        capabilityChildren.set(leaf.Capability, []);
      }
      capabilityChildren.get(leaf.Capability)!.push(leafKey);
    }
  }

  // Collect all unique capability names from metrics
  const capabilityNames = new Set<string>();
  for (const m of metrics) {
    const bc = m['Business Capability'];
    if (bc) capabilityNames.add(bc);
  }

  for (const capabilityName of capabilityNames) {
    // For each category, collect ALL non-zero percentage values (native + child)
    const categoryValues = new Map<string, number[]>();
    const categoriesWithMetrics = new Set<string>();
    let capHasTimeseriesData = false;

    // 2a. Native percentages: non-dimensional metrics belonging to this capability
    let nativeMetricsCount = 0;
    let nativeMetricsWithProgress = 0;
    
    for (const metric of metrics) {
      if ((metric['Business Capability'] || '') !== capabilityName) continue;
      const metricCategory = (metric.metricCategory || '').toLowerCase();
      if (!metricCategory || !(metricCategory in CATEGORY_FIELDS)) continue;

      // Only non-dimensional metrics are "native" to the capability
      if (metric.metricDimension) continue;

      nativeMetricsCount++;
      categoriesWithMetrics.add(metricCategory);
      const calculation = allMetricCalculations.get(getMetricCompositeKey(metric.metricValueKey, metric.Id));
      if (calculation?.hasChartData) capHasTimeseriesData = true;
      if (!calculation?.targetProgress) continue;

      const pctValue = calculation.targetProgress.percentage;
      if (!pctValue || pctValue === 0) continue; // skip zero, null, undefined

      nativeMetricsWithProgress++;

      if (!categoryValues.has(metricCategory)) {
        categoryValues.set(metricCategory, []);
      }
      categoryValues.get(metricCategory)!.push(pctValue);
    }

    // 2b. Child percentages: from already-computed dimension-item PercentageMetrics
    const childNames = capabilityChildren.get(capabilityName);
    let childNodesProcessed = 0;
    let childNodesWithData = 0;
    
    if (childNames) {
      for (const childName of childNames) {
        childNodesProcessed++;
        const childPct = result.get(childName);
        if (!childPct) {
          continue;
        }
        
        childNodesWithData++;
        if (childPct.hasTimeseriesData) capHasTimeseriesData = true;

        // Track which categories have metrics from children
        if (childPct.hasTimeMetrics) categoriesWithMetrics.add('time');
        if (childPct.hasVolumeMetrics) categoriesWithMetrics.add('volume');
        if (childPct.hasQualityMetrics) categoriesWithMetrics.add('quality');
        if (childPct.hasSentimentMetrics) categoriesWithMetrics.add('sentiment');
        if (childPct.hasCostMetrics) categoriesWithMetrics.add('cost');
        if (childPct.hasRiskMetrics) categoriesWithMetrics.add('risk');

        for (const [category, field] of Object.entries(CATEGORY_FIELDS)) {
          const value = childPct[field];
          if (!value || value === 0) continue; // skip zero, null, undefined
          if (!categoryValues.has(category)) {
            categoryValues.set(category, []);
          }
          categoryValues.get(category)!.push(value);
        }
      }
    }
    // 2c. Blend: average all non-zero values per category
    const capPct = new PercentageMetrics(
      0, 0, 0, 0, 0, 0, 
      capHasTimeseriesData,
      categoriesWithMetrics.has('time'),
      categoriesWithMetrics.has('volume'),
      categoriesWithMetrics.has('quality'),
      categoriesWithMetrics.has('sentiment'),
      categoriesWithMetrics.has('cost'),
      categoriesWithMetrics.has('risk')
    );
    if (categoryValues.size > 0) {
      for (const [category, values] of categoryValues) {
        const field = CATEGORY_FIELDS[category];
        if (field && values.length > 0) {
          const sum = values.reduce((a, b) => a + b, 0);
          const average = Math.round(sum / values.length);
          capPct[field] = average;
        }
      }
    }
    result.set(capabilityName, capPct);
  }

  return result;
}
